import React from 'react';
import styles from './dbInfo.module.scss';
import { useTranslation } from 'react-i18next';
import { CustomAccordion } from '@/components/cliv-materialUi/accordion';
import { IndexedDBDataType, useIndexedDBInfo } from '@/hooks/useIndexedDBInfo';
import { AppConstants } from '@/app.constants';
import { deleteDB } from 'idb';
import { ROUTES } from '@/route/routes.constants';
import { Link } from 'react-router';
import { InspectionCardItem } from '@/features/inspection-list/components/InspectionCardItem';
import { Inspection } from '@/types/Inspection.type';
import { getProductDisplayText } from '@/utils/inspection.utils';
import { convertToDateFormat } from '@/utils/date.utils';
import { idbStorage } from '@/store/idbStorage/idbStorage';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/rootReducer';
import { systemConfig } from '@/config/systemConfig';
import { logInfo } from '@/utils/apiLogger';
import { formatBytes, computeStringBytes } from '@/utils/bytes.util';
import { ReactComponent as DownloadIcon } from '@/assets/icons/download.svg';

const ALL_INDEX_DB_STORES = Object.values(AppConstants.INDEX_DB.store);

interface StoreAccordionConfig {
  label: string;
  data: Array<{ key: string; value: string }>;
  size: string;
  storeName: string;
  confirmText: string;
  successText: string;
  errorText: string;
}

export const DbInfo: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    dbInfo,
    imagesData,
    inspectionsData,
    metaData,
    savedInspectionsData,
    openedInspectionData,
    locationVerificationDraftsData,
    factoryVisitSessionsData,
    openSyncLogsData,
    pendingLogsData,
    imagesSize,
    inspectionsSize,
    metaDataSize,
    savedInspectionsSize,
    openedInspectionSize,
    locationVerificationDraftsSize,
    factoryVisitSessionsSize,
    openSyncLogsSize,
    pendingLogsSize,
    idbQuota,
    idbUsage,
    loading,
    error,
    refresh,
  } = useIndexedDBInfo();
  const loggedInUserName = useSelector((state: RootState) => state.auth.user?.name ?? 'Unknown');
  const loggedInUser = useSelector((state: RootState) => state.auth.user);
  const authToken = useSelector((state: RootState) => state.auth.token || '');
  const isServiceWorkerSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const [isServiceWorkerActive, setIsServiceWorkerActive] = React.useState(false);

  React.useEffect(() => {
    if (!isServiceWorkerSupported) {
      setIsServiceWorkerActive(false);
      return;
    }
    try {
      if (navigator.serviceWorker.controller) {
        setIsServiceWorkerActive(true);
      }
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => setIsServiceWorkerActive(!!reg && !!reg.active))
        .catch(() => {});
      navigator.serviceWorker.ready
        .then((reg) => setIsServiceWorkerActive(!!reg && !!reg.active))
        .catch(() => {});
    } catch {}
  }, [isServiceWorkerSupported]);

  const forceFullReload = () => {
    setTimeout(() => {
      try {
        window.location.reload();
      } catch {}
      try {
        window.location.assign(window.location.href);
      } catch {}
      try {
        window.location.href = window.location.href;
      } catch {}
    }, 50);
  };

  const handleDeleteDatabase = async () => {
    if (
      window.confirm(
        translate(
          'Are you sure you want to delete the entire database? This action cannot be undone and will remove all data.'
        )
      )
    ) {
      try {
        await Promise.all(ALL_INDEX_DB_STORES.map((store) => idbStorage.clear(store)));

        await deleteDB(AppConstants.INDEX_DB.dbName);
        console.log('Database deleted successfully');
        alert(translate('Database deleted successfully.'));

        await refresh();
        forceFullReload();
      } catch (error) {
        console.error('Error deleting database:', error);
        alert(translate('Error deleting database'));
      }
    }
  };

  const handleRefreshDatabase = async () => {
    try {
      await refresh();
      console.log('Database data refreshed');
      alert(translate('Database data refreshed successfully'));
      forceFullReload();
    } catch (error) {
      console.error('Error refreshing database:', error);
      alert(translate('Error refreshing database data'));
    }
  };

  const handleSendDetails = async () => {
    try {
      // Gather images (names from keys, sizes computed from values)
      const [
        imageKeys,
        imageValues,
        savedInspections,
        openedInspection,
        locationVerificationDrafts,
        factoryVisitSessions,
        openSyncLogs,
        pendingLogs,
      ] = await Promise.all([
        idbStorage.getAllKeys(AppConstants.INDEX_DB.store.images),
        idbStorage.getAll(AppConstants.INDEX_DB.store.images),
        idbStorage.getAll(AppConstants.INDEX_DB.store.inspections),
        idbStorage.getAll(AppConstants.INDEX_DB.store.masterData),
        idbStorage.getAll(AppConstants.INDEX_DB.store.savedInspections),
        idbStorage.getAll(AppConstants.INDEX_DB.store.openedInspection),
        idbStorage.getAll(AppConstants.INDEX_DB.store.locationVerificationDrafts),
        idbStorage.getAll(AppConstants.INDEX_DB.store.factoryVisitSessions),
        idbStorage.getAll(AppConstants.INDEX_DB.store.openSyncLogs),
        idbStorage.getAll(AppConstants.INDEX_DB.store.pendingLogs),
      ]);

      const files = (imageKeys || []).map((k: unknown, idx: number) => {
        const keyStr = typeof k === 'string' ? k : String(k);
        const val = (imageValues || [])[idx];
        const sizeBytes =
          typeof val === 'string'
            ? computeStringBytes(val)
            : computeStringBytes(JSON.stringify(val ?? ''));
        return { file: keyStr, size: formatBytes(sizeBytes) };
      });
      const totalImageBytes = files.reduce((sum, f) => {
        const [num, unit] = f.size.split(' ');
        const value = parseFloat(num);
        const factor =
          unit === 'GB'
            ? 1024 * 1024 * 1024
            : unit === 'MB'
              ? 1024 * 1024
              : unit === 'KB'
                ? 1024
                : 1;
        return sum + value * factor;
      }, 0);

      const payload = {
        user: loggedInUser,
        Images: {
          size: imagesSize || formatBytes(totalImageBytes),
          files,
          count: files.length,
        },
        Inspections: {},
        Metadata: {},
        savedInspect: savedInspections,
        openedInspection,
        locationVerificationDrafts,
        factoryVisitSessions,
        openSyncLogs,
        pendingLogs,
        indexDB: {
          origin: dbInfo.origin,
          bucketName: dbInfo.bucketName,
          isPersistent: dbInfo.isPersistent,
          durability: dbInfo.durability,
          inspectionSize: dbInfo.quota,
          expiration: dbInfo.expiration,
          version: dbInfo.version,
          objectStores: dbInfo.objectStores,
          swSupported: isServiceWorkerSupported ? 'Yes' : 'No',
          swActive: isServiceWorkerActive ? 'Yes' : 'No',
          idbQuota,
          idbUsage,
          sizes: {
            imagesSize,
            inspectionsSize,
            metaDataSize,
            savedInspectionsSize,
            openedInspectionSize,
            locationVerificationDraftsSize,
            factoryVisitSessionsSize,
            openSyncLogsSize,
            pendingLogsSize,
          },
          counts: {
            images: imagesData.length,
            inspections: inspectionsData.length,
            metaData: metaData.length,
            savedInspections: savedInspectionsData.length,
            openedInspection: openedInspectionData.length,
            locationVerificationDrafts: locationVerificationDraftsData.length,
            factoryVisitSessions: factoryVisitSessionsData.length,
            openSyncLogs: openSyncLogsData.length,
            pendingLogs: pendingLogsData.length,
          },
          timestamp: new Date().toISOString(),
        },
        appVersion: systemConfig.version,
      };

      console.log('payload of db info', payload);

      logInfo(systemConfig.apiUrl, authToken, loggedInUser || null, 'DB Details', payload);
      alert(translate('Details sent to server'));
    } catch (error) {
      console.error('Error sending details:', error);
      alert(translate('Error preparing details'));
    }
  };

  const clearStore = async (
    store: string,
    confirmText: string,
    successText: string,
    errorText: string
  ) => {
    if (window.confirm(translate(confirmText))) {
      try {
        await idbStorage.clear(store);
        alert(translate(successText));
        await refresh();
      } catch (error) {
        console.error('Error clearing store:', error);
        alert(translate(errorText));
      }
    }
  };

  const handleDeleteUnusedImages = async () => {
    if (!imagesData.length) return;
    const confirmMsg =
      'Are you sure you want to delete unused images? Images linked to saved inspections will be kept.';
    if (!window.confirm(translate(confirmMsg))) return;

    try {
      // Collect saved inspection IDs
      const savedInspectionIds = new Set(
        (savedInspectionsData || []).map((item) =>
          typeof item.key === 'string' ? item.key : String(item.key)
        )
      );

      // Get all image keys
      const allImageKeys = await idbStorage.getAllKeys(AppConstants.INDEX_DB.store.images);
      const keysToDelete = (allImageKeys || []).filter((k: unknown) => {
        if (typeof k !== 'string') return true; // non-string keys are considered unused
        // Keep if it matches any saved inspection id as prefix `${id}_`
        for (const id of savedInspectionIds) {
          if (k.startsWith(`${id}_`)) return false;
        }
        return true;
      }) as string[];

      if (keysToDelete.length > 0) {
        await idbStorage.removeMultipleItems(AppConstants.INDEX_DB.store.images, keysToDelete);
        alert(translate('Unused images deleted successfully'));
      } else {
        alert(translate('No unused images found'));
      }
      await refresh();
    } catch (error) {
      console.error('Error deleting unused images:', error);
      alert(translate('Error deleting unused images'));
    }
  };

  const renderStoreAccordion = ({
    label,
    data,
    size,
    storeName,
    confirmText,
    successText,
    errorText,
  }: StoreAccordionConfig) => (
    <div className={styles.accordionContainer}>
      <CustomAccordion
        header={
          <div className={styles.accordionHeader}>
            <span>
              {translate(label)} ({data.length})
            </span>
            <div className={styles.headerActions}>
              <span className={styles.sizeLabel}>{size}</span>
              {data.length > 0 && (
                <button
                  className={styles.clearButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearStore(storeName, confirmText, successText, errorText);
                  }}
                >
                  {translate('Clear all')}
                </button>
              )}
            </div>
          </div>
        }
        summary={renderTable(data)}
        isexpanded={false}
      />
    </div>
  );

  const renderTable = (data: Array<{ key: string; value: string }>) => (
    <div className={styles.tableContainer}>
      {loading ? (
        <div className={styles.loadingMessage}>
          <p>{translate('Loading data...')}</p>
        </div>
      ) : (
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>{translate('S.NO.')}</th>
              <th>{translate('Key')}</th>
              <th>{translate('Value')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.key}</td>
                <td>{loading ? 'Loading...' : getValue(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (error) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <h2 className={styles.header}>{translate('Database Info')}</h2>
          <Link className={styles.backButton} to={ROUTES.INSPECTIONS}>
            {translate('Back')}
          </Link>
        </div>
        <div className={styles.errorMessage}>
          <p>Error loading database information: {error}</p>
          <button onClick={handleRefreshDatabase} className={styles.refreshButton}>
            {translate('Retry')}
          </button>
        </div>
      </div>
    );
  }

  const handleDownloadImage = (imageDataUrl: string) => {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = imageDataUrl;
      link.download = `image_${Date.now()}.png`; // Generate a unique filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
      alert(translate('Error downloading image'));
    }
  };

  const handleDeleteSavedInspection = async (inspectionId: string) => {
    if (
      window.confirm(
        translate(
          'Are you sure you want to delete this saved inspection and all related data (including images)?'
        )
      )
    ) {
      try {
        // Remove the saved inspection and the main inspection record
        await Promise.all([
          idbStorage.removeItem(AppConstants.INDEX_DB.store.savedInspections, inspectionId),
          idbStorage.removeItem(AppConstants.INDEX_DB.store.inspections, inspectionId),
        ]);

        // Remove related images for this inspection
        const allImageKeys = await idbStorage.getAllKeys(AppConstants.INDEX_DB.store.images);
        const keysToDelete = (allImageKeys || []).filter(
          (k: any) => typeof k === 'string' && k.startsWith(`${inspectionId}_`)
        ) as string[];
        if (keysToDelete.length > 0) {
          await idbStorage.removeMultipleItems(AppConstants.INDEX_DB.store.images, keysToDelete);
        }

        alert(translate('Saved inspection and related data deleted successfully'));
        await refresh();
      } catch (error) {
        console.error('Error deleting saved inspection and related data:', error);
        alert(translate('Error deleting saved inspection and related data'));
      }
    }
  };

  function getValue(item: {
    key: string;
    value: string;
    type?: IndexedDBDataType;
  }): React.ReactNode | Iterable<React.ReactNode> {
    switch (item.type) {
      case IndexedDBDataType.Image:
        return (
          <div className={styles.imageCell}>
            <img src={item.value} alt={item.key} className={styles.image} />
            <DownloadIcon
              className={styles.downloadIcon}
              onClick={() => handleDownloadImage(item.value)}
            />
          </div>
        );
      case IndexedDBDataType.Inspection:
        const inspection = JSON.parse(item.value) as Inspection;
        return (
          <div className={styles.rowCell}>
            <div className={styles.rowContent}>
              <span>Product: {getProductDisplayText(inspection?.product)}</span>
              <span>Supplier: {inspection.supplier?.name ?? 'Unknown'}</span>
              <span>Inspection Date: {convertToDateFormat(inspection.inspectionDate)}</span>
              <div>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  to={`${ROUTES.INSPECTIONS}/${item.key}`}
                >
                  <InspectionCardItem {...(JSON.parse(item.value) as Inspection)} />
                </Link>
              </div>
            </div>
            <button
              className={styles.deleteRowButton}
              onClick={() => handleDeleteSavedInspection(item.key)}
            >
              {translate('Clear')}
            </button>
          </div>
        );

      default:
        return item.value;
    }
  }
  return (
    <div className={styles.formContainer}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{translate('Database Info')}</h2>
        <Link className={styles.backButton} to={ROUTES.INSPECTIONS}>
          {translate('Back')}
        </Link>
      </div>

      {/* Dropdown Sections */}
      <div className={styles.dropdownSection}>
        <div className={styles.accordionContainer}>
          <CustomAccordion
            header={
              <div className={styles.accordionHeader}>
                <span>
                  {translate('Images')} ({imagesData.length})
                </span>
                <div className={styles.headerActions}>
                  <span className={styles.sizeLabel}>{imagesSize}</span>
                  {imagesData.length > 0 && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUnusedImages();
                      }}
                    >
                      {translate('Delete Unused Images')}
                    </button>
                  )}
                </div>
              </div>
            }
            summary={
              <div>
                <div className={styles.summaryHeader}>
                  <span>
                    {translate('Total Images')}: {imagesData.length}
                  </span>
                </div>
                {renderTable(imagesData)}
              </div>
            }
            isexpanded={false}
          />
        </div>
        <div className={styles.accordionContainer}>
          <CustomAccordion
            header={
              <div className={styles.accordionHeader}>
                <span>{translate('Inspections')}</span>
                <div className={styles.headerActions}>
                  <span className={styles.sizeLabel}>{inspectionsSize}</span>
                  {inspectionsData.length > 0 && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearStore(
                          AppConstants.INDEX_DB.store.inspections,
                          'Are you sure you want to delete all inspections? This action cannot be undone.',
                          'All inspections have been deleted successfully',
                          'Error deleting inspections'
                        );
                      }}
                    >
                      {translate('Clear all')}
                    </button>
                  )}
                </div>
              </div>
            }
            summary={renderTable(inspectionsData)}
            isexpanded={false}
          />
        </div>
        <div className={styles.accordionContainer}>
          <CustomAccordion
            header={
              <div className={styles.accordionHeader}>
                <span>{translate('MetaData')}</span>
                <div className={styles.headerActions}>
                  <span className={styles.sizeLabel}>{metaDataSize}</span>
                  {metaData.length > 0 && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearStore(
                          AppConstants.INDEX_DB.store.masterData,
                          'Are you sure you want to delete all metadata? This action cannot be undone.',
                          'All metadata have been deleted successfully',
                          'Error deleting metadata'
                        );
                      }}
                    >
                      {translate('Clear all')}
                    </button>
                  )}
                </div>
              </div>
            }
            summary={renderTable(metaData)}
            isexpanded={false}
          />
        </div>
        <div className={styles.accordionContainer}>
          <CustomAccordion
            header={
              <div className={styles.accordionHeader}>
                <span>{translate('Saved Inspections')}</span>
                <div className={styles.headerActions}>
                  <span className={styles.sizeLabel}>{savedInspectionsSize}</span>
                  {savedInspectionsData.length > 0 && (
                    <button
                      className={styles.clearButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearStore(
                          AppConstants.INDEX_DB.store.savedInspections,
                          'Are you sure you want to delete all saved inspections? This action cannot be undone.',
                          'All saved inspections have been deleted successfully',
                          'Error deleting saved inspections'
                        );
                      }}
                    >
                      {translate('Clear all')}
                    </button>
                  )}
                </div>
              </div>
            }
            summary={renderTable(savedInspectionsData)}
            isexpanded={false}
          />
        </div>
        {renderStoreAccordion({
          label: 'Opened Inspection',
          data: openedInspectionData,
          size: openedInspectionSize,
          storeName: AppConstants.INDEX_DB.store.openedInspection,
          confirmText:
            'Are you sure you want to delete all opened inspections? This action cannot be undone.',
          successText: 'All opened inspections have been deleted successfully',
          errorText: 'Error deleting opened inspections',
        })}
        {renderStoreAccordion({
          label: 'Location Verification Drafts',
          data: locationVerificationDraftsData,
          size: locationVerificationDraftsSize,
          storeName: AppConstants.INDEX_DB.store.locationVerificationDrafts,
          confirmText:
            'Are you sure you want to delete all location verification drafts? This action cannot be undone.',
          successText: 'All location verification drafts have been deleted successfully',
          errorText: 'Error deleting location verification drafts',
        })}
        {renderStoreAccordion({
          label: 'Factory Visit Sessions',
          data: factoryVisitSessionsData,
          size: factoryVisitSessionsSize,
          storeName: AppConstants.INDEX_DB.store.factoryVisitSessions,
          confirmText:
            'Are you sure you want to delete all factory visit sessions? This action cannot be undone.',
          successText: 'All factory visit sessions have been deleted successfully',
          errorText: 'Error deleting factory visit sessions',
        })}
        {renderStoreAccordion({
          label: 'Open Sync Logs',
          data: openSyncLogsData,
          size: openSyncLogsSize,
          storeName: AppConstants.INDEX_DB.store.openSyncLogs,
          confirmText:
            'Are you sure you want to delete all open sync logs? This action cannot be undone.',
          successText: 'All open sync logs have been deleted successfully',
          errorText: 'Error deleting open sync logs',
        })}
        {renderStoreAccordion({
          label: 'Pending Logs',
          data: pendingLogsData,
          size: pendingLogsSize,
          storeName: AppConstants.INDEX_DB.store.pendingLogs,
          confirmText:
            'Are you sure you want to delete all pending logs? This action cannot be undone.',
          successText: 'All pending logs have been deleted successfully',
          errorText: 'Error deleting pending logs',
        })}
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Logged In User')}:</p>
          <p className={styles.value}>{loggedInUserName}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('App Version')}:</p>
          <p className={styles.value}>{systemConfig.version}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Environment')}:</p>
          <p className={styles.value}>{systemConfig.environment}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('DB Quota')}:</p>
          <p className={styles.value}>{idbQuota}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('DB Usage')}:</p>
          <p className={styles.value}>{idbUsage}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('SW Supported')}:</p>
          <p className={styles.value}>{isServiceWorkerSupported ? 'Yes' : 'No'}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('SW Active')}:</p>
          <p className={styles.value}>{isServiceWorkerActive ? 'Yes' : 'No'}</p>
        </div>
        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Origin')}:</p>
          <p className={styles.value}>{loading ? 'Loading...' : dbInfo.origin}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Bucket name')}:</p>
          <p className={styles.value}>{dbInfo.bucketName}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Is persistent')}:</p>
          <p className={styles.value}>{dbInfo.isPersistent ? 'Yes' : 'No'}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Durability')}:</p>
          <p className={styles.value}>{dbInfo.durability}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Inspection Size')}:</p>
          <p className={styles.value}>{loading ? 'Loading...' : dbInfo.quota}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Expiration')}:</p>
          <p className={styles.value}>{dbInfo.expiration}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Version')}:</p>
          <p className={styles.value}>{dbInfo.version}</p>
        </div>

        <div className={styles.gridRow}>
          <p className={styles.heading}>{translate('Object stores')}:</p>
          <p className={styles.value}>{dbInfo.objectStores}</p>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <button className={styles.detailsButton} onClick={handleSendDetails}>
          {translate('Send Details')}
        </button>
        <button className={styles.deleteButton} onClick={handleDeleteDatabase}>
          {translate('Delete database')}
        </button>
        <button className={styles.refreshButton} onClick={handleRefreshDatabase}>
          {translate('Refresh database')}
        </button>
      </div>
    </div>
  );
};
